import { Select } from 'antd';
import { Route } from 'antd/lib/breadcrumb/Breadcrumb';
import { TreeShaped, User } from 'domains/appDomain';
import { OrientationEnum } from 'domains/skuDomain';
import { FileItem } from 'domains/workspaceDetailDomain';
import { capitalize, keyBy, toLower } from 'lodash-es';
import { Moment } from 'moment';
import { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { UPLOAD_FILE_ADDRESS_PREFIX } from './constants';
import { LabeledValue } from 'antd/lib/select';

type TreeNode = {
  id: number | string;
  parentId: number | string;
};
export const listToTree = <T extends TreeNode>(
  arr: T[],
  cacheMap?: { [id: string]: TreeShaped<T> }
) => {
  let cMap =
    cacheMap ||
    keyBy<TreeShaped<T>>(
      arr.map((item) => ({
        ...item,
        children: undefined,
      })),
      'id'
    );
  let node;

  let roots = [];
  for (let i = 0; i < arr.length; i += 1) {
    node = arr[i];
    if (node.parentId && cMap[node.parentId]) {
      if (!cMap[node.parentId].children) {
        cMap[node.parentId].children = [];
      }
      cMap[node.parentId].children!.push(cMap[node.id]);
    } else {
      roots.push(cMap[node.id]);
    }
  }

  return roots;
};

export function absPathRender(
  route: Route,
  params: any,
  routes: Array<Route>,
  paths: Array<string>
) {
  //renders absolute path
  const last = routes.indexOf(route) === routes.length - 1;
  return last ? (
    <span>{route.breadcrumbName}</span>
  ) : (
    <Link to={'/' + paths.join('/')}>{route.breadcrumbName}</Link>
  );
}

export const pipedStrToOptions = (value: string | undefined) => {
  const options: ReactElement[] = [];
  if (!value || value.length === 0) {
    return options;
  }
  value.split('|').forEach((s, i) =>
    options.push(
      <Select.Option key={i.toString()} value={s}>
        {s}
      </Select.Option>
    )
  );
  return options;
};

export const getFullName = (item: { firstName: string; lastName: string }) => {
  if (!item.firstName) {
    return item.lastName;
  }

  if (!item.lastName) {
    return item.firstName;
  }

  return `${item.firstName} ${item.lastName}`;
};

export const getFileType = (fileName: string) => {
  let fileType = fileName?.match(/\.(.*)/);

  if (fileType) {
    // console.log('got', fileType);
    return toLower(fileType[1]);
  } else {
    // console.log('not got');
    return '';
  }
};

export const getDecimalDigits = (num: number | string, minimumDigits = 2) => {
  return String(num ?? '')
    .replace(/^[^.]*\./, '')
    .padEnd(minimumDigits, '0');
};

export const getFriendlyDisplay = (str: string) => {
  return str
    ?.split('_')
    ?.map((item) => {
      if (item === 'cmyk') {
        return 'CMYK';
      }
      if (item === 'No Printing' || item === 'Black & White') {
        return item;
      }
      if (item && item.toLowerCase() === 'pms') {
        return 'PMS';
      }
      if (item && item.toLowerCase() === 'rfm') {
        return 'RFM';
      }
      if (item && item.toLowerCase() === 'rfqs') {
        return 'RFQs';
      }
      if (
        item &&
        item.toLowerCase().includes('po') &&
        item.toLowerCase() !== OrientationEnum.portrait
      ) {
        return item.toLowerCase().replace(/po/gi, 'P.O.');
      }
      return capitalize(item);
    })
    ?.join(' ');
};

export function toLocaleString(text: number | string, digit = 2, options = {}) {
  if (typeof text === 'string') {
    return text;
  }

  if (text) {
    return text.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: digit,
      ...options,
    });
  }

  return '';
}

export function sleep(t: number) {
  return new Promise((resolve: Function) => {
    setTimeout(() => resolve(), t);
  });
}

export function isSameUTCDay(d1: Moment, d2: Moment) {
  return d1.isSame(d2, 'day');
}

export function getR4PGNumbers(pageCount: number) {
  const r = pageCount % 4;

  if (pageCount - r <= 0) {
    return [4];
  }
  return [pageCount - r, pageCount - r + 4];
}

export function zenDeskCommandHandler(
  command: 'identify' | 'updateSettings',
  data: object
) {
  if (window.zE) {
    const zE = window.zE as any;
    zE('webWidget', command, data);
  }
}

export function addZenDesk(user: User) {
  zenDeskCommandHandler('identify', {
    name: getFullName(user) || user.userFullName,
    email: user.sub,
    organization: user.orgName,
  });

  zenDeskCommandHandler('updateSettings', {
    contactForm: {
      // attachments: false,
      // fields: [{ id: 'description', prefill: { '*': 'text' } }],
    },
  });
}

export function isFloat(number: number | string, maxLen?: number) {
  if (!isNaN(Number(number)) && number.toString().indexOf('.') > -1) {
    if (maxLen) {
      return number.toString().split('.')[1].length <= maxLen;
    }
    return true;
  }
  return true;
}

export const newFileObject = (
  fileName: string,
  filePath: string,
  jobId: number,
  canDelete: boolean = true
): FileItem => {
  const downloadUrl = UPLOAD_FILE_ADDRESS_PREFIX + filePath;
  return {
    id: jobId,
    thumbUrl: downloadUrl,
    name: fileName,
    uid: filePath,
    url: downloadUrl,
    status: 'done',
    createAt: '',
    creatorId: 0,
    fileName: fileName,
    fileUrl: downloadUrl,
    jobId,
    jobProofId: 0,
    skuId: 0,
    updateAt: '',
    path: filePath,
    urlPrefix: UPLOAD_FILE_ADDRESS_PREFIX,
    canDelete,
  };
};

export const downloadFileHandler = (url: string, name?: string) => {
  const a = document.createElement('a');
  a.href = url;
  a.target = '_blank';
  a.download = name || url;
  a.addEventListener('click', () => {
    a.remove();
  });
  document.body.appendChild(a);
  a.click();
};

/*
 * 数组字符串转为首字母大写字符串
 * eg: ['PROOF_NEED_REVISION', 'PROOF_APPROVED'] => 'Proof Need Revision, Proof Approved'
 * */
export const convertToTitleCase = (array: string[] | string): string => {
  let arr: string[] = [];
  if (typeof array === 'string') {
    arr = [array];
  }
  if (!arr || arr.length === 0) {
    return '';
  }
  const result: string[] = arr.map((str) => {
    if (!str) {
      return '';
    }
    const words: string[] = str?.toLowerCase().split('_');
    for (let i = 0; i < words.length; i++) {
      words[i] = words[i].charAt(0).toUpperCase() + words[i].slice(1);
      if (words[i] === 'Po') {
        words[i] = 'PO';
      }
    }
    return words.join(' ');
  });
  return result.join(', ');
};

export const stringArrayToSelectOptions = (
  arr?: string[],
  labelConvertToTitleCase?: boolean
): LabeledValue[] => {
  if (!arr) {
    return [];
  }
  return arr.map((item) => {
    if (labelConvertToTitleCase) {
      return { value: item, label: convertToTitleCase(item) };
    }
    return { value: item, label: item };
  });
};
